import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  HoverCard, HoverCardContent, HoverCardTrigger
} from '@/components/ui/hover-card';
import {
  Loader2, Trash2, Upload, FileText, Download, Eye, Maximize2,
  Sparkles, Pencil
} from 'lucide-react';

const AUTO_DOC_TYPE = '__auto__';
const DOC_TYPES = ['DO', 'IN TKT', 'OUT TKT', 'BOL', 'POD', 'Rate Sheet', 'Other'];

// Documents Tab — manages document uploads, previews, type editing, bulk type
// updates. Owns its own selection + bulk state; all CRUD callbacks come in
// via props so the parent stays in charge of network calls and state.
export const LoadDocumentsTab = ({
  documents, selectedDocType, setSelectedDocType, fileInputRef,
  uploadingDoc, onUpload, onDownload, onDelete, isPreviewable, formatDateTime,
  previewDoc, previewUrl, previewLoading, handlePreviewHover, handleOpenFullPreview,
  onPullTicket, pullingTicket,
  editingTypeId, setEditingTypeId, savingTypeId, onChangeDocType,
  onBulkChangeType,
}) => {
  const [selectedDocIds, setSelectedDocIds] = useState(new Set());
  const [bulkType, setBulkType] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleDoc = (id) => {
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected = documents.length > 0 && selectedDocIds.size === documents.length;
  const toggleAll = () => {
    setSelectedDocIds(allSelected ? new Set() : new Set(documents.map((d) => d.id)));
  };

  const runBulk = async () => {
    if (!bulkType || selectedDocIds.size === 0 || !onBulkChangeType) return;
    setBulkBusy(true);
    try {
      await onBulkChangeType(Array.from(selectedDocIds), bulkType);
      setSelectedDocIds(new Set());
      setBulkType('');
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={selectedDocType} onValueChange={setSelectedDocType}>
          <SelectTrigger className="bg-slate-950 border-slate-800 text-white w-40" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem
              key={AUTO_DOC_TYPE}
              value={AUTO_DOC_TYPE}
              className="text-white"
              data-testid="doc-type-auto"
            >
              <span className="inline-flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-violet-400" />
                Auto-detect
              </span>
            </SelectItem>
            {DOC_TYPES.map(type => (
              <SelectItem key={type} value={type} className="text-white" data-testid={`doc-type-${type.replace(/\s+/g, '-')}`}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input type="file" ref={fileInputRef} onChange={onUpload} className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx" multiple />
        <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadingDoc} className="bg-cyan-600 hover:bg-cyan-500" data-testid="upload-doc-btn">
          {uploadingDoc ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
          Upload Documents
        </Button>
        {onPullTicket && (
          <Button size="sm" onClick={onPullTicket} disabled={pullingTicket} className="bg-amber-600 hover:bg-amber-500" data-testid="pull-ticket-btn">
            {pullingTicket ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
            Pull Gate Ticket
          </Button>
        )}
      </div>

      {/* Bulk-edit toolbar (visible when at least one row is selected) */}
      {selectedDocIds.size > 0 && (
        <div className="flex items-center gap-3 bg-violet-900/20 border border-violet-800/40 rounded-md px-3 py-2" data-testid="bulk-action-bar">
          <span className="text-sm text-violet-200">
            {selectedDocIds.size} doc{selectedDocIds.size > 1 ? 's' : ''} selected
          </span>
          <Select value={bulkType} onValueChange={setBulkType}>
            <SelectTrigger className="w-40 h-8 bg-slate-950 border-slate-700 text-white text-xs" data-testid="bulk-type-select">
              <SelectValue placeholder="Set type to…" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800">
              {DOC_TYPES.map((t) => (
                <SelectItem key={t} value={t} className="text-white" data-testid={`bulk-type-${t.replace(/\s+/g, '-')}`}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            onClick={runBulk}
            disabled={!bulkType || bulkBusy}
            className="bg-violet-600 hover:bg-violet-500 h-8"
            data-testid="bulk-apply-btn"
          >
            {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
            Apply
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSelectedDocIds(new Set())}
            className="text-slate-300 hover:text-white h-8"
            data-testid="bulk-clear-btn"
          >
            Clear
          </Button>
        </div>
      )}

      {/* Documents List */}
      <Table className="data-table">
        <TableHeader>
          <TableRow className="border-slate-800">
            <TableHead className="text-slate-400 w-10">
              <Checkbox
                checked={allSelected}
                onCheckedChange={toggleAll}
                disabled={documents.length === 0}
                aria-label="Select all documents"
                data-testid="doc-select-all"
              />
            </TableHead>
            <TableHead className="text-slate-400">Type</TableHead>
            <TableHead className="text-slate-400">Filename</TableHead>
            <TableHead className="text-slate-400">Uploaded</TableHead>
            <TableHead className="text-slate-400">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents.length === 0 ? (
            <TableRow className="border-slate-800">
              <TableCell colSpan={5} className="text-center text-slate-500 py-8">No documents uploaded</TableCell>
            </TableRow>
          ) : (
            documents.map(doc => (
              <TableRow key={doc.id} className="border-slate-800">
                <TableCell>
                  <Checkbox
                    checked={selectedDocIds.has(doc.id)}
                    onCheckedChange={() => toggleDoc(doc.id)}
                    aria-label={`Select ${doc.filename}`}
                    data-testid={`doc-select-${doc.id}`}
                  />
                </TableCell>
                <TableCell>
                  {editingTypeId === doc.id ? (
                    <Select
                      value={doc.document_type || doc.type || 'Other'}
                      onValueChange={(v) => onChangeDocType?.(doc, v)}
                      open
                      onOpenChange={(o) => { if (!o) setEditingTypeId?.(null); }}
                    >
                      <SelectTrigger
                        className="h-7 w-32 bg-slate-950 border-slate-700 text-white text-xs"
                        data-testid={`type-edit-trigger-${doc.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        {DOC_TYPES.map((t) => (
                          <SelectItem
                            key={t}
                            value={t}
                            className="text-white"
                            data-testid={`type-edit-${doc.id}-${t.replace(/\s+/g, '-')}`}
                          >
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setEditingTypeId?.(doc.id)}
                      className="group inline-flex items-center gap-1.5"
                      title="Click to change type"
                      data-testid={`type-cell-${doc.id}`}
                      disabled={savingTypeId === doc.id}
                    >
                      <Badge variant="outline" className="text-cyan-400 border-cyan-500/50">
                        {doc.type || doc.document_type || 'OTHER'}
                      </Badge>
                      {doc.detection_source === 'ai' && (
                        <Sparkles className="h-3 w-3 text-violet-400" title="AI auto-detected" />
                      )}
                      {doc.detection_source === 'filename' && (
                        <FileText className="h-3 w-3 text-slate-500" title="Detected from filename" />
                      )}
                      {savingTypeId === doc.id ? (
                        <Loader2 className="h-3 w-3 animate-spin text-slate-400" />
                      ) : (
                        <Pencil className="h-3 w-3 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                      )}
                    </button>
                  )}
                </TableCell>
                <TableCell className="text-white">
                  <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-slate-500" />{doc.filename || 'Unknown'}</div>
                </TableCell>
                <TableCell className="text-slate-400 text-xs">{formatDateTime(doc.uploaded_at)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {isPreviewable(doc) ? (
                      <HoverCard openDelay={200} closeDelay={100}>
                        <HoverCardTrigger asChild>
                          <Button
                            variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-blue-400"
                            onMouseEnter={() => handlePreviewHover(doc)} onClick={() => handleOpenFullPreview(doc)}
                            data-testid={`preview-doc-${doc.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </HoverCardTrigger>
                        <HoverCardContent side="left" align="center" className="w-80 p-2 bg-slate-900 border-slate-700">
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-slate-400 truncate max-w-[200px]">{doc.filename}</span>
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-400 hover:text-white" onClick={() => handleOpenFullPreview(doc)}>
                                <Maximize2 className="h-3 w-3" />
                              </Button>
                            </div>
                            <div className="relative bg-slate-950 rounded-md overflow-hidden" style={{ height: '200px' }}>
                              {previewLoading && previewDoc?.id === doc.id ? (
                                <div className="flex items-center justify-center h-full"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
                              ) : previewUrl && previewDoc?.id === doc.id ? (
                                doc.content_type?.startsWith('image/') || doc.filename?.toLowerCase().match(/\.(png|jpg|jpeg|gif|webp)$/) ? (
                                  <img src={previewUrl} alt={doc.filename} className="w-full h-full object-contain" />
                                ) : (
                                  <object data={previewUrl} type="application/pdf" className="w-full h-full" style={{ background: 'white' }}>
                                    <div className="flex flex-col items-center justify-center h-full text-slate-500 bg-white p-2">
                                      <FileText className="h-6 w-6 mb-1 text-red-400" /><span className="text-xs text-slate-600">PDF</span>
                                    </div>
                                  </object>
                                )
                              ) : (
                                <div className="flex flex-col items-center justify-center h-full text-slate-500">
                                  <FileText className="h-8 w-8 mb-2" /><span className="text-xs">Hover to preview</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </HoverCardContent>
                      </HoverCard>
                    ) : (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-600 cursor-not-allowed" disabled><Eye className="h-4 w-4" /></Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-white" onClick={() => onDownload(doc)}><Download className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-400" onClick={() => onDelete(doc.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <p className="text-sm text-slate-500">{documents.length} document(s) uploaded</p>
    </div>
  );
};
